import md5 from 'md5';
import zlib from 'zlib';

import {
	parseEntityName,
} from 'Crow/Common/Crow.js';

/**
 * Replaces window.form.getValue().
 * Reads from a flat fd object using dot-notation entity names.
 *
 *   "m1.uuid"                → fd["m1.uuid"]
 *   "m1.uuid.col"            → fd["m1.uuid"]["col"]
 *   "m1.uuid.m2.uuid"        → fd["m1.uuid"]["m2.uuid"]
 *   "m1.uuid.m2.uuid.attr"   → fd["m1.uuid"]["m2.uuid"]["attr"]
 *
 * If entityName contains a wildcard (*), delegates to getMatchingVals.
 */
const getFDValue = (fd, entityName) => {
	if (!fd || !entityName) {
		return null;
	}

	if (~entityName.indexOf('*')) {
		return _getValues(fd, [entityName]);
	}

	let l = entityName.split('.').length;
	let [m1, m1UUID, m2, m2UUID, attr] = entityName.split('.');
	let m1Key = `${m1}.${m1UUID}`;
	let m2Key = `${m2}.${m2UUID}`;

	if (!fd[m1Key]) {
		return null;
	}

	try {
		if (l === 2) {
			return fd[m1Key];
		}
		if (l === 3) {
			let col = m2;
			return fd[m1Key][col];
		}
		if (l === 4) {
			if (!fd[m1Key][m2Key]) {
				return null;
			}
			return fd[m1Key][m2Key];
		}
		if (l === 5) {
			if (!fd[m1Key][m2Key]) {
				return null;
			}
			return fd[m1Key][m2Key][attr];
		}
	} catch (err) {
		console.error(err);
	}
};

/**
 * Build raw keys list from fd for pattern matching.
 */
const _makeRawKeys = (fd) => {
	let rawKeys = [];
	for (let [m1Key, m1Data] of Object.entries(fd)) {
		rawKeys.push(m1Key);
		if (typeof m1Data !== 'object' || m1Data === null) {
			continue;
		}
		for (let [m2Key, m2Data] of Object.entries(m1Data)) {
			if (!~m2Key.indexOf('.')) {
				// col
				rawKeys.push(`${m1Key}.${m2Key}`);
			} else {
				// rel
				rawKeys.push(`${m1Key}.${m2Key}`);
				if (typeof m2Data === 'object' && m2Data !== null) {
					for (let attr of Object.keys(m2Data)) {
						// attr
						rawKeys.push(`${m1Key}.${m2Key}.${attr}`);
					}
				}
			}
		}
	}
	return rawKeys;
};

/**
 * Replaces window.form.getMatchingKeys().
 * Pattern-matches against all keys in an fd object.
 * Uses regex: '.' → '\\.', '*' → '[A-Za-z0-9-_]+'
 */
const matchFDKeys = (fd, scopes) => {
	if (!fd) {
		return [];
	}

	if (!Array.isArray(scopes)) {
		scopes = [scopes];
	}

	let rawKeys = _makeRawKeys(fd);
	let matchingKeys = [];

	scopes.forEach((scope) => {
		let scopeStr = scope
			.replace(/\./g, '\\.')
			.replace(/\*/g, '[A-Za-z0-9-_]+');

		let scopeRE = new RegExp(`^${scopeStr}$`);

		rawKeys.forEach((key) => {
			if (scopeRE.test(key)) {
				matchingKeys.push(key);
			}
		});
	});

	return matchingKeys;
};

/**
 * Internal: get matching values for wildcard patterns (like window.form.getValues).
 */
const _getMatchingVals = (fd, matchingKeys) => {
	let matchingVals = {};
	matchingKeys.forEach((entityKey) => {
		let s = entityKey.split('.');
		let l = s.length;

		let [m1, m1ID, col] = s;
		let m1Key = `${m1}.${m1ID}`;

		if (l === 'm1.id.col'.split('.').length) {
			if (fd[m1Key]) {
				matchingVals[`${m1Key}.${col}`] = fd[m1Key][col];
			}
		}

		if (l >= 'm1.id.m2.id'.split('.').length) {
			let [, , m2, m2ID, attr] = s;
			let m2Key = `${m2}.${m2ID}`;

			matchingVals[`${m1Key}.${m2Key}`] = true;
			if (attr) {
				if (fd[m1Key] && fd[m1Key][m2Key] && typeof fd[m1Key][m2Key][attr] !== 'undefined') {
					matchingVals[`${m1Key}.${m2Key}.${attr}`] = fd[m1Key][m2Key][attr];
				}
			}
		}
	});

	return matchingVals;
};

const _getValues = (fd, scopes) => {
	let values = {};
	let matchingKeys = matchFDKeys(fd, scopes);
	let matchingVals = _getMatchingVals(fd, matchingKeys);
	for (let [k, v] of Object.entries(matchingVals)) {
		values[k] = {
			value: v,
		};
	}
	return values;
};

/**
 * Replaces window.form.postFormData() payload construction.
 * Converts a flat changes object (pendingUpdates) into DD format.
 * Includes template_skeleton gzip+base64 compression.
 *
 * Returns { data_format: 'DD', data: {...} }
 */
const buildDDPayload = (changes) => {
	if (!changes || Object.keys(changes).length === 0) {
		return null;
	}

	// Build newVals-style nested structure from flat pendingUpdates
	let newVals = {};
	for (let [key, val] of Object.entries(changes)) {
		let parts = key.split('.');
		let l = parts.length;

		let [m1, m1UUID, m2, m2UUID, attr] = parts;
		let m1Key = `${m1}.${m1UUID}`;

		if (!newVals[m1Key]) {
			newVals[m1Key] = {};
		}

		if (l === 3) {
			let col = m2;
			newVals[m1Key][col] = val;
		}
		if (l === 4) {
			let m2Key = `${m2}.${m2UUID}`;
			// Don't overwrite existing attrs object with a boolean relation marker
			if (val === true && typeof newVals[m1Key][m2Key] === 'object' && newVals[m1Key][m2Key] !== null) {
				// Attrs already set via l=5 processing; keep them intact
			} else {
				newVals[m1Key][m2Key] = val;
			}
		}
		if (l === 5) {
			let m2Key = `${m2}.${m2UUID}`;
			if (!newVals[m1Key][m2Key] || typeof newVals[m1Key][m2Key] !== 'object') {
				newVals[m1Key][m2Key] = {};
			}
			newVals[m1Key][m2Key][attr] = val;
		}
	}

	// Handle template_skeleton compression (always gzip+base64, matching old postFormData behavior)
	for (let m1Key of Object.keys(newVals)) {
		if (m1Key.startsWith('template_skeleton.')) {
			if (newVals[m1Key].data != null) {
				let rawData = typeof newVals[m1Key].data === 'string'
					? newVals[m1Key].data
					: JSON.stringify(newVals[m1Key].data);
				let data = zlib.gzipSync(rawData).toString('base64');
				let hash = md5(data);
				newVals[m1Key].data = data;
				newVals[m1Key].hash = hash;
			}
		}
	}

	// Convert newVals to DD format
	let dd = {};
	Object.entries(newVals).forEach(([m1Key, m1Data]) => {
		// cols
		Object.entries(m1Data).forEach(([col, val]) => {
			if (!~col.indexOf('.')) {
				let k = `${m1Key}.${col}`;
				dd[k] = val;
			}
		});
		// rels/attr
		Object.entries(m1Data).forEach(([m2Key, m2Data]) => {
			if (~m2Key.indexOf('.')) {
				let k = `${m1Key}.${m2Key}`;
				dd[k] = Boolean(newVals[m1Key][m2Key]);
				if (typeof m2Data === 'object' && m2Data !== null) {
					Object.entries(m2Data).forEach(([attr, val]) => {
						let k = `${m1Key}.${m2Key}.${attr}`;
						dd[k] = val;
					});
				}
			}
		});
	});

	return {
		data_format: 'DD',
		data: dd,
	};
};

export {
	getFDValue,
	matchFDKeys,
	buildDDPayload,
	parseEntityName,
};
